import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  sourcingAPI,
  SourcingRun,
  SourcingMatch,
  UnlockedContact,
  CreditUsage,
} from "../services/sourcingApi";
import {
  Search,
  Users,
  Globe,
  Brain,
  Play,
  Check,
  AlertCircle,
  Clock,
  RefreshCw,
  UserPlus,
  ChevronDown,
  ChevronUp,
  Loader,
  MapPin,
  Star,
  Lock,
  Unlock,
  Phone,
  Mail,
  Building2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import "./SourcingTab.css";

interface SourcingTabProps {
  jobId: number;
  jobTitle: string;
}

type TabPhase = "internal" | "external" | "semantic";

const PHASE_CONFIG: Record<
  TabPhase,
  { label: string; icon: React.ReactNode; source: string; description: string }
> = {
  internal: {
    label: "Internal Database",
    icon: <Users size={18} />,
    source: "ai_finder",
    description: "AI-powered matching against your existing candidate database",
  },
  external: {
    label: "External Sources",
    icon: <Globe size={18} />,
    source: "apollo",
    description: "Search professional networks for new candidate profiles",
  },
  semantic: {
    label: "Semantic Search",
    icon: <Brain size={18} />,
    source: "semantic_search",
    description: "Free-text similarity search using AI embeddings",
  },
};

function maskLastName(name: string): string {
  if (!name || name.length <= 3) return "***";
  return name.slice(0, 2) + "*".repeat(name.length - 3) + name.slice(-1);
}

function getInitials(firstName: string, lastName: string): string {
  return ((firstName?.[0] || "") + (lastName?.[0] || "")).toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    "#1e40af",
    "#7c3aed",
    "#059669",
    "#d97706",
    "#dc2626",
    "#0891b2",
    "#4f46e5",
    "#be185d",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const SourcingTab: React.FC<SourcingTabProps> = ({ jobId }) => {
  const queryClient = useQueryClient();

  const [activePhase, setActivePhase] = useState<TabPhase>("internal");
  const [semanticQuery, setSemanticQuery] = useState("");
  const [selectedMatches, setSelectedMatches] = useState<Set<number>>(
    new Set(),
  );
  const [expandedRuns, setExpandedRuns] = useState<Set<number>>(new Set());
  const [unlockedContacts, setUnlockedContacts] = useState<
    Map<string, UnlockedContact>
  >(new Map());

  // External matches filter/sort state
  const [externalSearchText, setExternalSearchText] = useState("");
  const [externalSortBy, setExternalSortBy] = useState<
    "score" | "title" | "company" | "location"
  >("score");
  const [externalSortDir, setExternalSortDir] = useState<"asc" | "desc">(
    "desc",
  );

  // Track previous run statuses to detect completions
  const prevRunStatusesRef = useRef<Record<number, string>>({});

  // Fetch sourcing runs
  const {
    data: runsData,
    isLoading: runsLoading,
    refetch: refetchRuns,
  } = useQuery(["sourcing-runs", jobId], () => sourcingAPI.getRuns(jobId), {
    refetchInterval: 5000,
    onSuccess: (data) => {
      const newRuns: SourcingRun[] = data?.data || [];
      const prev = prevRunStatusesRef.current;
      // If any run transitioned to "completed", refresh matches
      const justCompleted = newRuns.some(
        (r) =>
          r.status === "completed" && prev[r.id] && prev[r.id] !== "completed",
      );
      if (justCompleted) {
        queryClient.invalidateQueries(["sourcing-matches", jobId]);
      }
      // Update tracking
      const next: Record<number, string> = {};
      for (const r of newRuns) next[r.id] = r.status;
      prevRunStatusesRef.current = next;
    },
  });
  const runs: SourcingRun[] = runsData?.data || [];

  // Fetch matches for active phase
  const { data: matchesData, isLoading: matchesLoading } = useQuery(
    ["sourcing-matches", jobId, activePhase],
    () => sourcingAPI.getMatches(jobId, { phase: activePhase }),
    { keepPreviousData: true },
  );
  const matches: SourcingMatch[] = matchesData?.data || [];

  // Filtered and sorted external matches
  const filteredExternalMatches = useMemo(() => {
    if (activePhase !== "external") return matches;
    let result = matches;

    // Text filter
    if (externalSearchText.trim()) {
      const q = externalSearchText.toLowerCase();
      result = result.filter((m) => {
        const p = m.external_data;
        if (!p) return false;
        return (
          p.name?.toLowerCase().includes(q) ||
          p.firstName?.toLowerCase().includes(q) ||
          p.lastName?.toLowerCase().includes(q) ||
          p.title?.toLowerCase().includes(q) ||
          p.company?.toLowerCase().includes(q) ||
          p.location?.toLowerCase().includes(q)
        );
      });
    }

    // Sort
    result = [...result].sort((a, b) => {
      const pa = a.external_data;
      const pb = b.external_data;
      let cmp = 0;
      switch (externalSortBy) {
        case "score":
          cmp = (a.match_score || 0) - (b.match_score || 0);
          break;
        case "title":
          cmp = (pa?.title || "").localeCompare(pb?.title || "");
          break;
        case "company":
          cmp = (pa?.company || "").localeCompare(pb?.company || "");
          break;
        case "location":
          cmp = (pa?.location || "").localeCompare(pb?.location || "");
          break;
      }
      return externalSortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [
    matches,
    activePhase,
    externalSearchText,
    externalSortBy,
    externalSortDir,
  ]);

  // Fetch daily credit usage (only when on external phase)
  const { data: creditsData } = useQuery(
    ["sourcing-credits"],
    () => sourcingAPI.getDailyCredits(),
    { enabled: activePhase === "external", refetchInterval: 30000 },
  );
  const credits: CreditUsage | null = creditsData?.data || null;
  const isLimitReached = credits ? credits.remaining <= 0 : false;

  // Run mutation
  const runMutation = useMutation(
    (params: { phase: string; source: string; options?: unknown }) =>
      sourcingAPI.triggerRun(
        jobId,
        params.phase,
        params.source,
        params.options as Record<string, unknown>,
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sourcing-runs", jobId]);
        queryClient.invalidateQueries(["sourcing-matches", jobId]);
      },
    },
  );

  // Apply mutation
  const applyMutation = useMutation(
    (matchId: number) => sourcingAPI.applyFromMatch(matchId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sourcing-matches", jobId]);
      },
    },
  );

  // Bulk apply mutation
  const bulkApplyMutation = useMutation(
    (matchIds: number[]) => sourcingAPI.bulkApply(matchIds),
    {
      onSuccess: () => {
        setSelectedMatches(new Set());
        queryClient.invalidateQueries(["sourcing-matches", jobId]);
      },
    },
  );

  // Import mutation
  const importMutation = useMutation(
    (params: { matchId: number; createApplication: boolean }) =>
      sourcingAPI.importProfile(
        params.matchId,
        jobId,
        params.createApplication,
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sourcing-matches", jobId]);
      },
    },
  );

  // Stable key for external match Apollo IDs — avoids re-fetching on every poll cycle
  const externalApolloIdKey =
    activePhase === "external"
      ? matches
          .map((m) => m.external_profile_id)
          .filter(Boolean)
          .join(",")
      : "";

  // Hydrate unlocked contacts from DB when external matches load
  useEffect(() => {
    if (!externalApolloIdKey) return;
    const apolloIds = externalApolloIdKey.split(",");

    sourcingAPI
      .getUnlockedContacts(apolloIds)
      .then((result) => {
        if (result.success && result.data.length > 0) {
          setUnlockedContacts((prev) => {
            const next = new Map(prev);
            for (const unlock of result.data) {
              next.set(unlock.apollo_id, unlock);
            }
            return next;
          });
        }
      })
      .catch(() => {
        // Non-critical — cards still work, just without unlock state hydration
      });
  }, [externalApolloIdKey]);

  const handleRunPhase = useCallback(() => {
    const config = PHASE_CONFIG[activePhase];
    const options =
      activePhase === "semantic" ? { query: semanticQuery, topK: 20 } : {};
    runMutation.mutate({
      phase: activePhase,
      source: config.source,
      options,
    });
  }, [activePhase, semanticQuery, runMutation]);

  const handleToggleSelect = (matchId: number) => {
    setSelectedMatches((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) {
        next.delete(matchId);
      } else {
        next.add(matchId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const eligibleIds = matches
      .filter((m) => m.candidate_id && !m.action_taken)
      .map((m) => m.id);
    if (selectedMatches.size === eligibleIds.length) {
      setSelectedMatches(new Set());
    } else {
      setSelectedMatches(new Set(eligibleIds));
    }
  };

  const tabRuns = runs.filter((r) => r.phase === activePhase);
  const latestRun = tabRuns[0];
  const isRunning =
    latestRun?.status === "running" || latestRun?.status === "pending";

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <Check size={14} className="status-icon completed" />;
      case "running":
      case "pending":
        return <Loader size={14} className="status-icon running spin" />;
      case "failed":
        return <AlertCircle size={14} className="status-icon failed" />;
      default:
        return <Clock size={14} className="status-icon" />;
    }
  };

  return (
    <div className="sourcing-tab">
      {/* Pipeline Progress */}
      <div className="pipeline-progress">
        {(
          Object.entries(PHASE_CONFIG) as [
            TabPhase,
            (typeof PHASE_CONFIG)[TabPhase],
          ][]
        ).map(([phase, config]) => {
          const phaseRuns = runs.filter((r) => r.phase === phase);
          const completed = phaseRuns.some((r) => r.status === "completed");
          const running = phaseRuns.some(
            (r) => r.status === "running" || r.status === "pending",
          );
          const totalMatches =
            phase === activePhase && matches.length > 0
              ? matches.length
              : phaseRuns.reduce((sum, r) => sum + (r.results_count || 0), 0);

          return (
            <div
              key={phase}
              role="button"
              tabIndex={0}
              className={`pipeline-step ${completed ? "completed" : ""} ${running ? "running" : ""} ${activePhase === phase ? "active" : ""}`}
              onClick={() => setActivePhase(phase)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActivePhase(phase);
                }
              }}
            >
              <div className="step-icon">{config.icon}</div>
              <div className="step-info">
                <span className="step-label">{config.label}</span>
                {completed && (
                  <span className="step-count">{totalMatches} matches</span>
                )}
                {running && <span className="step-status">Running...</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Phase Content */}
      <div className="sourcing-content">
        <div className="tab-header">
          <div className="tab-info">
            <h3>
              {PHASE_CONFIG[activePhase].icon} {PHASE_CONFIG[activePhase].label}
            </h3>
            <p>{PHASE_CONFIG[activePhase].description}</p>
          </div>

          <div className="tab-actions">
            {activePhase === "semantic" && (
              <input
                type="text"
                className="semantic-input"
                placeholder="Enter search query (e.g. 'Senior React developer with AWS experience')"
                value={semanticQuery}
                onChange={(e) => setSemanticQuery(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && semanticQuery && handleRunPhase()
                }
              />
            )}
            <button
              className="run-button"
              onClick={handleRunPhase}
              disabled={
                runMutation.isLoading ||
                isRunning ||
                (activePhase === "semantic" && !semanticQuery)
              }
            >
              {runMutation.isLoading || isRunning ? (
                <>
                  <Loader size={16} className="spin" /> Running...
                </>
              ) : (
                <>
                  <Play size={16} />{" "}
                  {tabRuns.length > 0 ? "Re-run Search" : "Run Search"}
                </>
              )}
            </button>
            <button
              className="refresh-button"
              onClick={() => {
                refetchRuns();
                queryClient.invalidateQueries(["sourcing-matches", jobId]);
              }}
              title="Refresh results"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedMatches.size > 0 && (
          <div className="bulk-actions">
            <span>{selectedMatches.size} selected</span>
            <button
              className="bulk-apply-button"
              onClick={() => bulkApplyMutation.mutate([...selectedMatches])}
              disabled={bulkApplyMutation.isLoading}
            >
              <UserPlus size={14} /> Create Applications ({selectedMatches.size}
              )
            </button>
          </div>
        )}

        {/* Run History */}
        {tabRuns.length > 0 && (
          <div className="run-history">
            <h4>Run History</h4>
            {tabRuns.map((run) => (
              <div key={run.id} className="run-item">
                <div
                  className="run-summary"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    setExpandedRuns((prev) => {
                      const next = new Set(prev);
                      if (next.has(run.id)) next.delete(run.id);
                      else next.add(run.id);
                      return next;
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setExpandedRuns((prev) => {
                        const next = new Set(prev);
                        if (next.has(run.id)) next.delete(run.id);
                        else next.add(run.id);
                        return next;
                      });
                    }
                  }}
                >
                  {getStatusIcon(run.status)}
                  <span className="run-date">
                    {new Date(run.created_at).toLocaleString()}
                  </span>
                  <span className="run-results">
                    {run.results_count} results
                  </span>
                  {run.created_by_name && (
                    <span className="run-user">by {run.created_by_name}</span>
                  )}
                  {expandedRuns.has(run.id) ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </div>
                {expandedRuns.has(run.id) && run.error_message && (
                  <div className="run-error">{run.error_message}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Matches Table/Cards */}
        <div className="matches-section">
          {matchesLoading || runsLoading ? (
            <div className="loading-state">
              <Loader size={24} className="spin" />
              <span>Loading matches...</span>
            </div>
          ) : matches.length === 0 ? (
            <div className="empty-state">
              <Search size={48} />
              <h3>No matches yet</h3>
              <p>
                Click &ldquo;Run Search&rdquo; to find candidates for this job.
              </p>
            </div>
          ) : activePhase === "external" ? (
            // External: Card layout
            <div className="matches-cards">
              {matches.length > 0 && (
                <div className="matches-header-row">
                  <span className="match-count">
                    {filteredExternalMatches.length} of {matches.length}{" "}
                    profiles
                  </span>
                  {credits && (
                    <span
                      className={`credit-counter ${isLimitReached ? "limit-reached" : ""}`}
                    >
                      <Lock size={14} />
                      {credits.used}/{credits.limit} credits used today
                      {isLimitReached && " (limit reached)"}
                    </span>
                  )}
                </div>
              )}
              <div className="sourcing-filter-toolbar">
                <div className="filter-search">
                  <Search size={14} />
                  <input
                    type="text"
                    placeholder="Filter by name, title, company, location..."
                    value={externalSearchText}
                    onChange={(e) => setExternalSearchText(e.target.value)}
                    className="filter-search-input"
                  />
                </div>
                <div className="filter-sort">
                  <select
                    value={externalSortBy}
                    onChange={(e) =>
                      setExternalSortBy(
                        e.target.value as
                          | "score"
                          | "title"
                          | "company"
                          | "location",
                      )
                    }
                    className="filter-sort-select"
                  >
                    <option value="score">Score</option>
                    <option value="title">Title</option>
                    <option value="company">Company</option>
                    <option value="location">Location</option>
                  </select>
                  <button
                    className="filter-sort-dir"
                    onClick={() =>
                      setExternalSortDir((d) => (d === "asc" ? "desc" : "asc"))
                    }
                    title={`Sort ${externalSortDir === "asc" ? "descending" : "ascending"}`}
                  >
                    {externalSortDir === "asc" ? (
                      <ArrowUp size={14} />
                    ) : (
                      <ArrowDown size={14} />
                    )}
                  </button>
                </div>
              </div>
              {filteredExternalMatches.map((match) => (
                <ExternalMatchCard
                  key={match.id}
                  match={match}
                  jobId={jobId}
                  unlockedContact={
                    match.external_profile_id
                      ? unlockedContacts.get(match.external_profile_id)
                      : undefined
                  }
                  onUnlock={(apolloId, contact) =>
                    setUnlockedContacts((prev) =>
                      new Map(prev).set(apolloId, contact),
                    )
                  }
                  onImport={(createApp) =>
                    importMutation.mutate({
                      matchId: match.id,
                      createApplication: createApp,
                    })
                  }
                  isImporting={importMutation.isLoading}
                  isLimitReached={isLimitReached}
                  onCreditsInvalidate={() =>
                    queryClient.invalidateQueries(["sourcing-credits"])
                  }
                />
              ))}
            </div>
          ) : (
            // Internal & Semantic: Table layout
            <div className="matches-table-wrapper">
              <table className="matches-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          selectedMatches.size > 0 &&
                          selectedMatches.size ===
                            matches.filter(
                              (m) => m.candidate_id && !m.action_taken,
                            ).length
                        }
                      />
                    </th>
                    <th>Candidate</th>
                    <th>Score</th>
                    <th>Rating</th>
                    <th>Skill</th>
                    <th>Location</th>
                    <th>Strengths</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((match) => (
                    <InternalMatchRow
                      key={match.id}
                      match={match}
                      selected={selectedMatches.has(match.id)}
                      onToggle={() => handleToggleSelect(match.id)}
                      onApply={() => applyMutation.mutate(match.id)}
                      isApplying={applyMutation.isLoading}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Internal/Semantic match table row
const InternalMatchRow: React.FC<{
  match: SourcingMatch;
  selected: boolean;
  onToggle: () => void;
  onApply: () => void;
  isApplying: boolean;
}> = ({ match, selected, onToggle, onApply, isApplying }) => {
  const name =
    match.first_name || match.last_name
      ? `${match.first_name || ""} ${match.last_name || ""}`.trim()
      : `Candidate #${match.candidate_id}`;

  const getRatingColor = (rating: string | null) => {
    switch (rating) {
      case "excellent":
        return "#16a34a";
      case "good":
        return "#2563eb";
      case "fair":
        return "#d97706";
      case "poor":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  return (
    <tr className={match.action_taken ? "actioned" : ""}>
      <td>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={!!match.action_taken || !match.candidate_id}
        />
      </td>
      <td>
        <a
          href={`/candidates/${match.candidate_id}`}
          className="candidate-link"
          onClick={(e) => e.stopPropagation()}
        >
          {name}
        </a>
      </td>
      <td>
        <span className="score-badge">
          {match.match_score != null ? Math.round(match.match_score) : "-"}
        </span>
      </td>
      <td>
        <span
          className="rating-badge"
          style={{ color: getRatingColor(match.rating) }}
        >
          {match.rating || "-"}
        </span>
      </td>
      <td>{match.main_skill || "-"}</td>
      <td>{match.candidate_location || "-"}</td>
      <td className="strengths-cell">
        {match.strengths?.slice(0, 2).join(", ") || "-"}
      </td>
      <td>
        {match.action_taken === "applied" ? (
          <span className="applied-badge">
            <Check size={14} /> Applied
          </span>
        ) : (
          <button
            className="apply-button"
            onClick={(e) => {
              e.stopPropagation();
              onApply();
            }}
            disabled={isApplying || !match.candidate_id}
            title="Create Application"
          >
            <UserPlus size={14} /> Apply
          </button>
        )}
      </td>
    </tr>
  );
};

// External match card with unlock/deepsearch/google actions
const ExternalMatchCard: React.FC<{
  match: SourcingMatch;
  jobId: number;
  unlockedContact?: UnlockedContact;
  onUnlock: (apolloId: string, contact: UnlockedContact) => void;
  onImport: (createApplication: boolean) => void;
  isImporting: boolean;
  isLimitReached: boolean;
  onCreditsInvalidate: () => void;
}> = ({
  match,
  unlockedContact,
  onUnlock,
  onImport,
  isImporting,
  isLimitReached,
  onCreditsInvalidate,
}) => {
  const profile = match.external_data;
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isDeepsearching, setIsDeepsearching] = useState(false);
  const [deepsearchStatus, setDeepsearchStatus] = useState<string | null>(null);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<
    "unlock" | "deepsearch" | null
  >(null);

  if (!profile) return null;

  const apolloId = match.external_profile_id;
  const isUnlocked = !!unlockedContact;
  const hasEmail =
    !!profile.email ||
    (profile.emailStatus != null && profile.emailStatus !== "unavailable");

  const displayFirstName = isUnlocked
    ? unlockedContact.first_name
    : profile.firstName;
  const displayLastName = isUnlocked
    ? unlockedContact.last_name
    : maskLastName(profile.lastName);
  const displayEmail = isUnlocked ? unlockedContact.email : null;
  const displayPhone = isUnlocked ? unlockedContact.phone : null;
  const displayLinkedin = isUnlocked
    ? unlockedContact.linkedin_url
    : profile.linkedinUrl;

  const initials = getInitials(profile.firstName, profile.lastName);
  const avatarColor = getAvatarColor(
    `${profile.firstName} ${profile.lastName}`,
  );

  const handleUnlock = async () => {
    if (!apolloId || isUnlocking) return;
    setIsUnlocking(true);
    setUnlockError(null);
    try {
      const result = await sourcingAPI.unlockContact(apolloId);
      if (result.success) {
        onUnlock(apolloId, result.data);
        onCreditsInvalidate();
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { code?: string; error?: string } };
      };
      if (axiosErr.response?.data?.code === "DAILY_LIMIT_EXCEEDED") {
        setUnlockError("Daily credit limit reached. Try again tomorrow.");
        onCreditsInvalidate();
      } else {
        setUnlockError("Failed to unlock contact. Please try again.");
      }
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleDeepsearch = async () => {
    if (!apolloId || isDeepsearching) return;
    setIsDeepsearching(true);
    setDeepsearchStatus(null);
    try {
      const result = await sourcingAPI.deepsearchContact(apolloId);
      if (result.success) {
        setDeepsearchStatus(result.status);
        if (result.status === "success") {
          onUnlock(apolloId, result.data);
        }
        onCreditsInvalidate();
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { code?: string } } };
      if (axiosErr.response?.data?.code === "DAILY_LIMIT_EXCEEDED") {
        setUnlockError("Daily credit limit reached. Try again tomorrow.");
        onCreditsInvalidate();
      } else {
        setDeepsearchStatus("unsuccessful");
      }
    } finally {
      setIsDeepsearching(false);
    }
  };

  const handleGoogleSearch = () => {
    const query = encodeURIComponent(
      `${displayFirstName} ${displayLastName} ${profile.company} ${profile.title}`,
    );
    window.open(`https://www.google.com/search?q=${query}`, "_blank");
  };

  const companySuffix = profile.company ? " " + profile.company : "";
  const linkedinSearchName = `${profile.firstName} ${profile.lastName}${companySuffix}`;
  const maskedFullName = `${profile.firstName} ${maskLastName(profile.lastName)}`;

  const getConfirmDialogBody = () => {
    if (confirmDialog === "unlock") {
      if (hasEmail) {
        return (
          <>
            Unlock full contact details for <strong>{maskedFullName}</strong>{" "}
            including verified email address, phone number, and LinkedIn
            profile. This will consume 1 credit.
          </>
        );
      }
      return (
        <>
          No verified email address is available for this contact. Unlocking
          will reveal the full name and LinkedIn profile URL (if available).
          This will consume 1 credit.
        </>
      );
    }
    return (
      <>
        HireFocuZ will attempt to deepsearch contact information and acquire a
        valid email address for <strong>{maskedFullName}</strong>. This may take
        a moment to process. This operation will consume up to 2 credits.
        Success is not guaranteed.
      </>
    );
  };
  const linkedinSearchUrl = `https://linkedin.com/search/results/people/?keywords=${encodeURIComponent(linkedinSearchName)}`;

  return (
    <div className={`external-card ${isUnlocked ? "unlocked" : "locked"}`}>
      {(isUnlocking || isDeepsearching) && (
        <div className="card-loading-overlay">
          <Loader size={20} className="spin" />
          <span>{isDeepsearching ? "Deep searching..." : "Unlocking..."}</span>
        </div>
      )}
      {confirmDialog && (
        <div className="card-confirm-overlay">
          <div className="confirm-dialog-content">
            <h4 className="confirm-dialog-title">
              {confirmDialog === "unlock"
                ? "Unlock Contact Information"
                : "Deepsearch & Unlock Contact Information"}
            </h4>
            <p className="confirm-dialog-body">{getConfirmDialogBody()}</p>
            <div className="confirm-dialog-actions">
              <button
                className="confirm-cancel-btn"
                onClick={() => setConfirmDialog(null)}
              >
                Cancel
              </button>
              <button
                className={
                  confirmDialog === "unlock"
                    ? "confirm-action-btn unlock"
                    : "confirm-action-btn deepsearch"
                }
                onClick={() => {
                  const action =
                    confirmDialog === "unlock"
                      ? handleUnlock
                      : handleDeepsearch;
                  setConfirmDialog(null);
                  action();
                }}
              >
                {confirmDialog === "unlock"
                  ? "Unlock (1 credit)"
                  : "Deepsearch (up to 2 credits)"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card-header">
        <div className="card-header-left">
          <div
            className="initials-avatar"
            style={{ backgroundColor: avatarColor }}
          >
            {initials}
          </div>
          <div className="profile-info">
            <h4>
              {displayFirstName} {displayLastName}
            </h4>
            <span className="profile-title">{profile.title}</span>
            {profile.company && (
              <span className="profile-company">
                <Building2 size={12} /> {profile.company}
              </span>
            )}
            {profile.apolloData?.headline &&
              profile.apolloData.headline !== profile.title && (
                <span className="profile-headline">
                  {profile.apolloData.headline}
                </span>
              )}
          </div>
        </div>
      </div>

      <div className="card-meta">
        {profile.location && (
          <span>
            <MapPin size={14} /> {profile.location}
          </span>
        )}
        <span
          className={`contact-field ${displayEmail ? "revealed" : "locked-field"}`}
        >
          <Mail size={14} /> {displayEmail || "Email locked"}
        </span>
        <span
          className={`contact-field ${displayPhone ? "revealed" : "locked-field"}`}
        >
          <Phone size={14} /> {displayPhone || "Phone locked"}
        </span>
      </div>

      {deepsearchStatus === "unsuccessful" && (
        <div className="deepsearch-unsuccessful">
          <AlertCircle size={14} /> Deepsearch unsuccessful -- email could not
          be derived or validated
        </div>
      )}

      {unlockError && (
        <div className="deepsearch-unsuccessful">
          <AlertCircle size={14} /> {unlockError}
        </div>
      )}

      {profile.skills && profile.skills.length > 0 && (
        <div className="card-skills">
          {profile.skills.slice(0, 3).map((skill) => (
            <span key={skill} className="skill-tag">
              {skill}
            </span>
          ))}
          {profile.skills.length > 3 && (
            <span className="skill-more">+{profile.skills.length - 3}</span>
          )}
        </div>
      )}

      <div className="card-actions">
        {match.action_taken === "imported" ||
        match.action_taken === "applied" ? (
          <span className="applied-badge">
            <Check size={14} />{" "}
            {match.action_taken === "applied" ? "Applied" : "Imported"}
          </span>
        ) : isUnlocked ? (
          <>
            <button
              className="import-button"
              onClick={() => onImport(false)}
              disabled={isImporting}
            >
              <UserPlus size={14} /> Import to DB
            </button>
            <button
              className="import-apply-button"
              onClick={() => onImport(true)}
              disabled={isImporting}
            >
              <Star size={14} /> Import & Apply
            </button>
          </>
        ) : (
          <>
            <button
              className="unlock-button"
              onClick={() => setConfirmDialog("unlock")}
              disabled={
                isUnlocking || isDeepsearching || isLimitReached || !apolloId
              }
              title={
                isLimitReached
                  ? "Daily credit limit reached"
                  : "Unlock contact (1 credit)"
              }
            >
              <Unlock size={14} /> Unlock (1 credit)
            </button>
            <button
              className="deepsearch-button"
              onClick={() => setConfirmDialog("deepsearch")}
              disabled={
                isUnlocking || isDeepsearching || isLimitReached || !apolloId
              }
              title={
                isLimitReached
                  ? "Daily credit limit reached"
                  : "Deepsearch contact (1-2 credits)"
              }
            >
              <Search size={14} /> Deepsearch
            </button>
          </>
        )}
        <button
          className="gsearch-button"
          onClick={handleGoogleSearch}
          title="Search on Google (no credits)"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Search
        </button>
        {(displayLinkedin || profile.firstName) && (
          <a
            href={displayLinkedin || linkedinSearchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="linkedin-button"
            title={
              displayLinkedin ? "View LinkedIn Profile" : "Search on LinkedIn"
            }
          >
            {displayLinkedin ? "LinkedIn" : "LI Search"}
          </a>
        )}
      </div>
    </div>
  );
};

export default SourcingTab;
